import moment from 'moment'

import { USER_STATES } from 'constants/index'
import * as types from './types'
import { apiPools } from '../../api'
import { success as userUpdate } from './conversations/user'

const startFetchUsersRequest = () => ({ type: types.FETCH_USERS_REQUEST })
const fetchUsersFailure = (error) => ({ type: types.FETCH_USERS_FAILURE, error })

/**
 * Dispatch user success
 * @param {{users: [object], size: number}} data
 * @param {boolean} isInfiniteScroll
 *
 * @returns {}
 */
function usersSuccess(payload, isInfiniteScroll) {
  const data = {
    type: types.FETCH_USERS_SUCCESS,
    users: payload.users,
    size: payload.size,
    isInfiniteScroll
  }

  if (payload.page) data.page = payload.page

  return data
}

export const fetchUsersSuccess = (data, isInfiniteScroll) => (dispatch, getState) => {
  const { user: { _id, frontOptions }, tabs } = getState()

  const channelsFilter = Array.isArray(frontOptions?.poolChannels)
    ? frontOptions?.poolChannels?.map(c => !['facebook_post', 'instagram_post'].includes(c.value) ? c.value.split('_')[0] : c.value)
    : []
  const typeFilter = frontOptions?.poolSelectType?.value
  const isAsc = frontOptions?.poolOrder?.value === 'asc'

  const {
    switchPending: isPendingMsg,
    poolNotAssigned: isNotAssigned,
    poolMainOutbound: isOutbound,
    poolBrandsAndDelegations = {},
    poolCategories = []
  } = frontOptions || {}
  const delegations = (poolBrandsAndDelegations?.delegations || []).map(d => d.value || d)
  const delegationSize = delegations.length
  const withoutCategory = poolCategories.some(elem => elem === 'label_without_category')
  const categorySize = withoutCategory ? poolCategories.length - 1 : poolCategories.length

  data.users = data.users.filter(user => {
    const {
      id, state, notInPool, pendingMsgCount, isRead, assignedTo = [], categories = [],
      outboundAt, drafts, lastMsgType, channels = [], delegation, channelRemove
    } = user

    if (channelRemove) return false
    if (assignedTo.includes(_id)) return false
    if (USER_STATES.isNotPool.includes(state)) return false
    if (notInPool) return false
    if (delegationSize && !delegations.includes(delegation)) return false


    // Unread filter
    const withPendingMsg = pendingMsgCount > 0 || !isRead
    if (isPendingMsg && !withPendingMsg) return false

    // Not assigned filter
    if (isNotAssigned && assignedTo.length) return false

    // Outbound filter
    if (isOutbound && !outboundAt) return false

    // Type filter
    if (typeFilter) {
      if (typeFilter === 'draft' && !drafts) return false
      if (typeFilter === 'sent' && !lastMsgType.includes('operator')) return false
      if (typeFilter === 'received' && !lastMsgType.includes('customer')) return false
      if (typeFilter === 'openedTabs' && !tabs.users.includes(id)) return false
    }

    // Channels filter
    if (channelsFilter?.length) {
      const channel = channels[0] || {}

      if (channel.provider === 'chatmail') {
        if (!channelsFilter.includes(channel.provider)) return false
      } else if (channel.channel === 'wall') {
        if (!channelsFilter.includes(channel.connectionType.split('_')[0])) return false
      } else {
        if (!channelsFilter.includes(channel.channel)) return false
      }
    }

    if (!withoutCategory && categorySize) {
      if (!poolCategories.some(cat => categories.includes(cat))) return false
    }

    if (withoutCategory && !categorySize) {
      if (categories?.length) return false
    }

    if (categorySize && withoutCategory) {
      const userCategoriesSize = categories?.length

      if (userCategoriesSize) {
        if (!poolCategories.some(cat => categories.includes(cat))) return false
      }
    }

    return true
  }).sort((a, b) => {
    return isAsc
      ? moment(a.lastMessageSentAt || a.updatedAt) - moment(b.lastMessageSentAt || b.updatedAt)
      : moment(b.lastMessageSentAt || b.updatedAt) - moment(a.lastMessageSentAt || a.updatedAt)
  })

  dispatch(usersSuccess(data, isInfiniteScroll))
}

/**
 * Get users to general pool
 * @param {object} payload
 * @param {number} [payload.page=0] Page number
 * @param {number} [payload.limit=100]
 * @param {boolean} [payload.withNotAssigned=false]
 * @param {boolean} [payload.withPendingMsg=false]
 * @param {boolean} [payload.outbound=false]
 * @param {boolean} [isInfiniteScroll]
 *
 * @returns {Promise<{users: [Users], size: number}>}
 */
export const fetchUsers = (payload = {}, isInfiniteScroll = false) => async (dispatch, getState) => {
  const {
    order,
    withPendingMsg,
    withNotAssigned,
    categories,
    outbound,
    selectType,
    channels,
    brands,
    delegations,
    page = 0
  } = payload

  try {
    const { user: { frontOptions }, tabs } = getState()

    if (isInfiniteScroll) dispatch(startFetchUsersRequest())

    /* ORDER FILTER */
    if (order === undefined && frontOptions.poolOrder) payload.order = frontOptions.poolOrder
    /* UNREAD FILTER */
    if (withPendingMsg === undefined && frontOptions.switchPending) payload.withPendingMsg = frontOptions.switchPending
    /* NOT ASSIGNED FILTER */
    if (withNotAssigned === undefined && frontOptions.poolNotAssigned) payload.withNotAssigned = frontOptions.poolNotAssigned
    /* CATEGORIES FILTER */
    if (categories === undefined && frontOptions.poolCategories?.length) payload.categories = frontOptions.poolCategories
    /* OUTBOUND FILTER */
    if (outbound === undefined && frontOptions.poolMainOutbound) payload.outbound = frontOptions.poolMainOutbound
    /* TYPE FILTER */
    if (selectType === undefined && frontOptions.poolSelectType) payload.selectType = frontOptions.poolSelectType
    const initialSelectType = payload.selectType?.value
    if (!['all', 'openedTabs'].includes(initialSelectType)) {
      payload.selectType = initialSelectType
    } else {
      payload.selectType = ''
    }
    /* CHANNELS FILTER */
    if (channels === undefined && frontOptions.poolChannels) payload.channels = frontOptions.poolChannels
    if (payload.channels?.length) {
      const channels = []
      payload.channels.map(channel => channels.push(channel.value))
      payload.channels = channels
    } else {
      payload.channels = []
    }
    /* BRANDS AND DELEGATIONS FILTER */
    // Brands
    if (brands === undefined && frontOptions.poolBrandsAndDelegations?.brands) payload.brands = frontOptions.poolBrandsAndDelegations.brands || []
    if (payload.brands?.length) {
      const brands = []
      payload.brands.map(brand => brands.push(brand.value))
      payload.brands = brands
    } else {
      payload.brands = []
    }
    // Delegations
    if (delegations === undefined && frontOptions.poolBrandsAndDelegations?.delegations) payload.delegations = frontOptions.poolBrandsAndDelegations.delegations || []
    if (payload.delegations?.length) {
      const delegations = []
      payload.delegations.map(delegation => delegations.push(delegation.value))
      payload.delegations = delegations
    } else {
      payload.delegations = []
    }

    if (payload.categories?.length) {
      payload.withoutCategory = payload.categories.some(elem => elem === 'label_without_category')
      payload.categories = payload.categories.filter(elem => elem !== 'label_without_category')
    }

    const { data } = await apiPools.mainUsers(payload)

    if (tabs.users.length) {
      data.users.forEach((user) => {
        if (tabs.users.some(e => user.id === e.id)) dispatch(userUpdate(user))
      })
    }

    let users = [...data.users]
    let size = data.size

    if (initialSelectType === 'openedTabs') {
      const openedUsers = []
      for (const openedUserId of tabs.users) {
        const openedUser = users.find(user => user.id === openedUserId)
        if (openedUser) openedUsers.push(openedUser)
      }
      users = [...openedUsers]
      size = openedUsers.length
    }

    dispatch(usersSuccess({ users, size, page: page || 0 }, isInfiniteScroll))

    return true
  } catch(error) {
    dispatch(fetchUsersFailure(error))
    return false
  }
}
